import React, { useState } from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  Grid,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Button,
  Card,
  CardContent,
  Typography,
  Collapse
} from '@mui/material';
import { ExpandMore, ExpandLess } from '@mui/icons-material';

const SearchComponent = ({ onSearch, onReset, title = "Search" }) => {
  const [expanded, setExpanded] = useState(false);
  const [searchCriteria, setSearchCriteria] = useState({
    projectCode: '',
    projectStatus: '',
    requestorName: '',
    budgetCode: '',
    modelName: '',
    personInCharge: '',
    startDate: '',
    endDate: '',
    searchType: 'contains' // starts with, contains, exactly
  });

  const handleInputChange = (field, value) => {
    setSearchCriteria(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSearch = () => {
    onSearch(searchCriteria);
  };

  const handleReset = () => {
    const resetCriteria = {
      projectCode: '',
      projectStatus: '',
      requestorName: '',
      budgetCode: '',
      modelName: '',
      personInCharge: '',
      startDate: '',
      endDate: '',
      searchType: 'contains'
    };
    setSearchCriteria(resetCriteria);
    onReset(resetCriteria);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleSearch();
    }
  };

  return (
    <Card sx={{ mb: 3 }} component="section" role="search" aria-labelledby="search-title">
        <CardContent>
          <Box 
            sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'space-between', 
              mb: 1,
              p: 1,
              borderRadius: 1,
              backgroundColor: 'primary.main',
              color: 'primary.contrastText',
              cursor: 'pointer'
            }}
            onClick={() => setExpanded(!expanded)}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                setExpanded(!expanded);
              }
            }}
            aria-expanded={expanded}
            aria-controls="search-form-content"
            aria-label={expanded ? 'Collapse search form' : 'Expand search form'}
          >
            <Typography variant="subtitle1" component="h2" id="search-title" sx={{ fontWeight: 500 }}>
              🔍 {title} {expanded ? '' : '(Click to expand)'}
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant="caption" sx={{ opacity: 0.9 }}>
                {expanded ? 'Hide' : 'Show'}
              </Typography>
              {expanded ? <ExpandLess /> : <ExpandMore />}
            </Box>
          </Box>
          
          <Collapse in={expanded} timeout={300} unmountOnExit>
            <Box sx={{ mt: 2, p: 2, backgroundColor: 'grey.50', borderRadius: 1 }}>
              <Grid 
                container 
                spacing={2} 
                role="form" 
                aria-label="Search form" 
                sx={{ alignItems: 'flex-end' }}
                id="search-form-content"
              >
            {/* Line 1: Start Date, End Date, Project Code, Project Status, Requestor Name */}
            <Grid item xs={12} sm={6} md={2.4}>
              <TextField
                fullWidth
                label="Start Date"
                type="date"
                value={searchCriteria.startDate}
                onChange={(e) => handleInputChange('startDate', e.target.value)}
                onKeyDown={handleKeyDown}
                size="small"
                InputLabelProps={{ shrink: true }}
                aria-describedby="start-date-help"
              />
              <div id="start-date-help" className="sr-only">
                Select the start date for filtering results
              </div>
            </Grid>
            <Grid item xs={12} sm={6} md={2.4}>
              <TextField
                fullWidth
                label="End Date"
                type="date"
                value={searchCriteria.endDate}
                onChange={(e) => handleInputChange('endDate', e.target.value)}
                onKeyDown={handleKeyDown}
                size="small"
                InputLabelProps={{ shrink: true }}
                aria-describedby="end-date-help"
              />
              <div id="end-date-help" className="sr-only">
                Select the end date for filtering results
              </div>
            </Grid>
            <Grid item xs={12} sm={6} md={2.4}>
              <TextField
                fullWidth
                label="Project Code"
                value={searchCriteria.projectCode}
                onChange={(e) => handleInputChange('projectCode', e.target.value)}
                onKeyDown={handleKeyDown}
                size="small"
              />
            </Grid>
            <Grid item xs={12} sm={6} md={2.4}>
              <TextField
                fullWidth
                label="Project Status"
                value={searchCriteria.projectStatus}
                onChange={(e) => handleInputChange('projectStatus', e.target.value)}
                onKeyDown={handleKeyDown}
                size="small"
              />
            </Grid>
            <Grid item xs={12} sm={6} md={2.4}>
              <TextField
                fullWidth
                label="Requestor Name"
                value={searchCriteria.requestorName}
                onChange={(e) => handleInputChange('requestorName', e.target.value)}
                onKeyDown={handleKeyDown}
                size="small"
              />
            </Grid>

            {/* Line 2: Budget Code, Model Name, Person In Charge, Search Type, Search & Reset Buttons */}
            <Grid item xs={12} sm={6} md={2.4}>
              <TextField
                fullWidth
                label="Budget Code"
                value={searchCriteria.budgetCode}
                onChange={(e) => handleInputChange('budgetCode', e.target.value)}
                onKeyDown={handleKeyDown}
                size="small"
              />
            </Grid>
            <Grid item xs={12} sm={6} md={2.4}>
              <TextField
                fullWidth
                label="Model Name"
                value={searchCriteria.modelName}
                onChange={(e) => handleInputChange('modelName', e.target.value)}
                onKeyDown={handleKeyDown}
                size="small"
              />
            </Grid>
            <Grid item xs={12} sm={6} md={2.4}>
              <TextField
                fullWidth
                label="Person In Charge"
                value={searchCriteria.personInCharge}
                onChange={(e) => handleInputChange('personInCharge', e.target.value)}
                onKeyDown={handleKeyDown}
                size="small"
              />
            </Grid>
            <Grid item xs={12} sm={6} md={2.4}>
              <FormControl fullWidth size="small">
                <InputLabel id="search-type-label">Search Type</InputLabel>
                <Select
                  value={searchCriteria.searchType}
                  label="Search Type"
                  onChange={(e) => handleInputChange('searchType', e.target.value)}
                  labelId="search-type-label"
                  aria-describedby="search-type-help"
                >
                  <MenuItem value="starts with">Starts With</MenuItem>
                  <MenuItem value="contains">Contains</MenuItem>
                  <MenuItem value="exactly">Exactly</MenuItem>
                </Select>
                <div id="search-type-help" className="sr-only">
                  Choose how to match your search terms
                </div>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={2.4}>
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-end' }}>
                <Button
                  variant="contained"
                  onClick={handleSearch}
                  sx={{ height: '40px', flex: 1 }}
                  type="button"
                  aria-label="Execute search with current criteria"
                >
                  Search
                </Button>
                <Button
                  variant="outlined"
                  onClick={handleReset}
                  sx={{ height: '40px', minWidth: 80 }}
                  type="button"
                  aria-label="Clear all search criteria"
                >
                  Reset
                </Button>
              </Box>
            </Grid>
              </Grid>
            </Box>
          </Collapse>
        </CardContent>
      </Card>
  );
};

SearchComponent.propTypes = {
  onSearch: PropTypes.func.isRequired,
  onReset: PropTypes.func.isRequired,
  title: PropTypes.string
};

export default SearchComponent;